import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { STATUS_LABELS, STATUS_COLORS, SHIPMENT_STATUS_LABELS, TRANSPORT_MODE_LABELS, TRANSPORT_MODE_COLORS } from '../../lib/constants';
import PackageStatusBadge from '../../components/PackageStatusBadge';
import { CountryRoute } from '../../components/CountryDisplay';
import ProfessionalLayout from '../../components/ProfessionalLayout';
import '../../styles/professional-theme.css';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import { Package, Ship, Users, CheckSquare, AlertTriangle, Eye, Weight, Search, Printer, ImageIcon, Megaphone, Calendar, ShieldCheck, RefreshCw, Plane, MapPinCheck, CircleCheckBig, Download, Pencil, FileSpreadsheet, ExternalLink, CheckCircle2, XCircle, Loader2, Zap, Camera, Trash2, Clock, PackageCheck } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { downloadFile } from '../../lib/download';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { DEFAULT_COUNTRIES, getFlagEmoji } from '../../lib/countries';
import SearchableSelect from '../../components/SearchableSelect';
import ShipmentScanDialog from '../../components/ShipmentScanDialog';
import SEOHead from '../../components/SEOHead';

// Statuts considérés comme terminaux : ces départs quittent la vue active
// pour être regroupés dans l'onglet "Historique".
const HISTORY_STATUSES = ['completed', 'cancelled'];

// Date limite de dépôt par défaut = date de départ − 1 jour, au format datetime-local.
const depositDeadlineFromDeparture = (departureLocal) => {
  if (!departureLocal) return '';
  const d = new Date(departureLocal);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() - 1);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};

const SHIPMENT_TRANSITIONS = {
  scheduled: ['in_transit', 'cancelled'],
  in_transit: ['arrived', 'delayed', 'cancelled'],
  delayed: ['arrived', 'in_transit', 'cancelled'],
  arrived: ['awaiting_delivery', 'cancelled'],
  awaiting_delivery: ['delivering', 'completed', 'cancelled'],
  delivering: ['completed', 'cancelled'],
};

const PACKAGE_TRANSITIONS = {
  registered: ['collected'],
  collected: ['in_transit'],
  in_transit: ['arrived'],
  arrived: ['awaiting_customs'],
  awaiting_customs: ['customs_cleared'],
  customs_cleared: ['awaiting_delivery'],
  awaiting_delivery: ['out_for_delivery'],
  out_for_delivery: ['delivered'],
  delivered: [],
};
import PackagePhotosDialog from '../../components/PackagePhotosDialog';
import ShipmentBroadcastDialog from '../../components/ShipmentBroadcastDialog';
import EmptyState from '../../components/EmptyState';
import { printPackageLabel } from '../../lib/packageLabel';
import GeographicSelector from '../../components/GeographicSelector';
import Pagination from '../../components/Pagination';
import PackageSearch from '../../components/PackageSearch';
import { getShipmentETA, daysSinceUpdate } from '../../lib/utils';

const DELETE_REASONS = {
  duplicate: 'Doublon — enregistré plusieurs fois',
  client_request: 'Demande du client — annulation',
  incorrect_data: 'Données incorrectes — erreur de saisie',
  refused_package: 'Colis refusé — conditions non respectées',
  damaged_package: 'Colis endommagé — non transportable',
  regulatory_issue: 'Problème réglementaire — contenu interdit',
  other: 'Autre motif — à préciser',
};

const ShipmentManagement = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  // Les admins peuvent corriger un départ même après le statut planifié (ex. en transit).
  const canEditInTransit = ['company_admin', 'super_admin', 'platform_admin'].includes(user?.role);
  // Une erreur de destinataire se découvre souvent une fois le départ parti : les
  // admins corrigent un colis à n'importe quel stade, le collecteur non.
  const canEditPackages = ['company_admin', 'country_admin', 'super_admin'].includes(user?.role);

  const publicCalendarUrl = user?.company_id ? `${window.location.origin}/departures/${user.company_id}` : '';
  const copyPublicCalendarLink = () => {
    if (!publicCalendarUrl) return;
    navigator.clipboard?.writeText(publicCalendarUrl)
      .then(() => toast({ title: 'Lien copié', description: publicCalendarUrl }))
      .catch(() => window.prompt('Copiez le lien du calendrier public :', publicCalendarUrl));
  };
  const [searchParams] = useSearchParams();
  const [groupedPackages, setGroupedPackages] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedPackages, setSelectedPackages] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [confirmAssign, setConfirmAssign] = useState(null); // { shipmentId, shipmentName }
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [previewImage, setPreviewImage] = useState(null); // aperçu plein écran d'une photo de profil
  // autoAssign: true = mode auto « Démarrer un chargement » (scan assigne + charge) ;
  // false = « Vérifier le chargement » (scan de contrôle des colis déjà assignés).
  const [scanDialog, setScanDialog] = useState({ open: false, checkpoint: 'loading', autoAssign: false });
  const [startLoadingOpen, setStartLoadingOpen] = useState(false);
  const [selectedShipment, setSelectedShipment] = useState(null);
  const [shipmentDetails, setShipmentDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [editShipment, setEditShipment] = useState({
    name: '',
    flight_name: '',
    origin_country: '',
    destination_country: '',
    departure_date: '',
    expected_arrival_date: '',
    deposit_deadline: '',
    notes: ''
  });
  
  // États pour la pagination des carréaisons
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  // Pagination dédiée à l'onglet Historique (indépendante des départs actifs)
  const [historyPage, setHistoryPage] = useState(1);
  
  // États pour la pagination des colis à collecter (par partenaire)
  const [packagePageByPartner, setPackagePageByPartner] = useState({});
  const [packageItemsPerPartner, setPackageItemsPerPartner] = useState({});
  
  const [newShipment, setNewShipment] = useState({
    name: '',
    flight_name: '',
    origin_country: '',
    destination_country: '',
    departure_date: '',
    expected_arrival_date: '',
    deposit_deadline: '',
    notes: '',
    transport_mode: ''
  });
  
  // Recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [openGlobalSearch, setOpenGlobalSearch] = useState(false);
  const [photosDialog, setPhotosDialog] = useState({ open: false, packageId: null, oceCode: '' });
  const [broadcastDialog, setBroadcastDialog] = useState({ open: false, shipmentId: null, shipmentName: '' });
  const [statusChanging, setStatusChanging] = useState(false);
  const [verifyingFlight, setVerifyingFlight] = useState(false);
  const [customsDialog, setCustomsDialog] = useState({ open: false, packageId: null, oceCode: '', note: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, pkg: null });
  const [deleteReason, setDeleteReason] = useState('duplicate');
  const [deleteCustom, setDeleteCustom] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [pkgEditDialog, setPkgEditDialog] = useState({ open: false, pkg: null });
  const [pkgEditForm, setPkgEditForm] = useState({});
  const [pkgEditSaving, setPkgEditSaving] = useState(false);
  const [customsBulkDialog, setCustomsBulkDialog] = useState({ open: false, shipmentId: null, shipmentName: '', packageIds: [], note: '' });
  const [packageStatusDialog, setPackageStatusDialog] = useState({ open: false, packageId: null, oceCode: '', currentStatus: '', newStatus: '', note: '' });
  const [shipmentStatusDialog, setShipmentStatusDialog] = useState({ open: false, shipmentId: null, shipmentName: '', currentStatus: '', newStatus: '', note: '' });
  const [trackingMode, setTrackingMode] = useState('manual');
  const { toast } = useToast();

  // Filtre par statut départ (from URL or default)
  const urlStatus = searchParams.get('status') || '';
  const SHIPMENT_STATUSES = ['scheduled', 'in_transit', 'arrived', 'completed', 'delayed', 'cancelled'];
  const PACKAGE_STATUSES = ['registered', 'collected', 'in_transit', 'arrived', 'awaiting_customs', 'customs_cleared', 'delivered'];
  const isShipmentStatus = SHIPMENT_STATUSES.includes(urlStatus);
  const isPackageStatus = PACKAGE_STATUSES.includes(urlStatus);
  const [shipmentStatusFilter, setShipmentStatusFilter] = useState(isShipmentStatus ? urlStatus : 'all');
  const [packageStatusFilter, setPackageStatusFilter] = useState(isPackageStatus ? urlStatus : 'all');
  const initialTab = isShipmentStatus ? 'departures' : 'packages';
  const [activeMainTab, setActiveMainTab] = useState(initialTab);

  // État pour la sélection géographique
  const [selectedDestinationCountry, setSelectedDestinationCountry] = useState('');
  const [selectedDestinationCity, setSelectedDestinationCity] = useState('');

  // Transport mode
  const [transportModes, setTransportModes] = useState(['aerien']);
  const [defaultTransportMode, setDefaultTransportMode] = useState('aerien');
  const [loadingMode, setLoadingMode] = useState('manual');
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [selectedTransportMode, setSelectedTransportMode] = useState('aerien');

  // Shipment status counts for filter buttons
  const shipmentStatusCounts = useMemo(() => {
    const counts = { all: shipments.length };
    for (const s of shipments) {
      const st = s.status === 'planned' ? 'scheduled' : s.status;
      counts[st] = (counts[st] || 0) + 1;
    }
    return counts;
  }, [shipments]);

  // Shipment KPIs
  const shipmentKpis = useMemo(() => {
    const total = shipments.length;
    const inTransit = shipments.filter(s => s.status === 'in_transit' || s.status === 'in_progress').length;
    const arrived = shipments.filter(s => s.status === 'arrived').length;
    const completed = shipments.filter(s => s.status === 'completed').length;
    return { total, inTransit, arrived, completed };
  }, [shipments]);

  const totalAwaitingCustoms = useMemo(() => {
    return shipments.reduce((sum, s) => sum + (s.status_counts?.awaiting_customs || 0), 0);
  }, [shipments]);

  const exportShipmentPackagesToCSV = (packages, shipmentName) => {
    const BOM = '\uFEFF';
    const headers = ['OCE', 'Expéditeur', 'Destinataire', 'Destination', 'Poids (kg)', 'Statut', 'Date création'];
    const escape = (val) => {
      const str = String(val ?? '');
      return str.includes(';') || str.includes('"') || str.includes('\n')
        ? `"${str.replace(/"/g, '""')}"`
        : str;
    };
    const rows = packages.map(pkg => [
      escape(pkg.oce_code),
      escape(pkg.sender_name),
      escape(pkg.recipient_name),
      escape(pkg.destination_country),
      escape(pkg.weight),
      escape((STATUS_LABELS[language] || STATUS_LABELS.fr)[pkg.status] || pkg.status),
      escape(pkg.created_at ? new Date(pkg.created_at).toLocaleDateString('fr-FR') : '')
    ].join(';'));

    const csv = BOM + [headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `colis_${shipmentName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printLabel = (pkg) => printPackageLabel(pkg, { logoUrl: companyLogo, companyName });

  const handleDeletePackage = async () => {
    if (!deleteDialog.pkg) return;
    if (deleteReason === 'other' && !deleteCustom.trim()) return;
    setDeleting(true);
    try {
      await api.delete(`/api/packages/${deleteDialog.pkg.id}`, {
        data: { deletion_reason: deleteReason, custom_reason: deleteReason === 'other' ? deleteCustom.trim() : null },
      });
      setDeleteDialog({ open: false, pkg: null });
      setDeleteReason('duplicate');
      setDeleteCustom('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const openPkgEditDialog = (pkg) => {
    setPkgEditForm({
      sender_name: pkg.sender_name || '',
      sender_phone: pkg.sender_phone || '',
      sender_email: pkg.sender_email || '',
      recipient_name: pkg.recipient_name || '',
      recipient_phone: pkg.recipient_phone || '',
      recipient_email: pkg.recipient_email || '',
      weight: pkg.weight ?? '',
      description: pkg.description || '',
      destination_country: pkg.destination_country || '',
      destination_city: pkg.destination_city || '',
    });
    setPkgEditDialog({ open: true, pkg });
  };

  const handleEditPackage = async () => {
    if (!pkgEditDialog.pkg) return;
    setPkgEditSaving(true);
    try {
      const payload = {};
      Object.entries(pkgEditForm).forEach(([k, v]) => {
        if (v === '' || v == null) return;
        payload[k] = k === 'weight' ? Number(v) : v;
      });
      await api.put(`/api/packages/${pkgEditDialog.pkg.id}`, payload);
      setPkgEditDialog({ open: false, pkg: null });
      setPkgEditForm({});
      fetchData();
      // La correction peut venir du détail d'un départ : cette liste doit suivre.
      if (openDetailsDialog && selectedShipment) fetchShipmentDetails(selectedShipment);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la modification');
    } finally {
      setPkgEditSaving(false);
    }
  };

  // ETA countdown auto-refresh
  const [, setEtaTick] = useState(0);

  useEffect(() => {
    fetchData();
    // Fetch transport modes
    api.get('/api/company/transport-modes')
      .then(res => {
        const modes = res.data.transport_modes || ['aerien'];
        const defMode = res.data.default_transport_mode || 'aerien';
        setTransportModes(modes);
        setDefaultTransportMode(defMode);
        setSelectedTransportMode(defMode);
        setTrackingMode(res.data.tracking_mode || 'manual');
        setLoadingMode(res.data.loading_mode || 'manual');
      })
      .catch(() => {});
    api.get('/api/company/settings')
      .then(res => {
        setCompanyLogo(res.data?.general?.logo_url || '');
        setCompanyName(res.data?.general?.company_name || 'FretManager');
      })
      .catch(() => {});
  }, []);

  // Auto-refresh on WebSocket shipment_status_updated event
  useEffect(() => {
    const handler = () => fetchData();
    window.addEventListener('shipment_status_updated', handler);
    return () => window.removeEventListener('shipment_status_updated', handler);
  }, []);

  // ETA countdown refresh every 60s
  useEffect(() => {
    const interval = setInterval(() => setEtaTick(t => t + 1), 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [packagesRes, shipmentsRes] = await Promise.all([
        api.get('/api/packages/grouped-by-partner'),
        api.get('/api/shipments')
      ]);
      setGroupedPackages(packagesRes.data);
      // Trier les carréaisons du plus récent au plus ancien
      const sortedShipments = shipmentsRes.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setShipments(sortedShipments);
    } catch (err) {
      console.error('Error fetching data:', err);
      if (err.response?.status === 403) {
        setError('Accès refusé - Permissions insuffisantes');
      } else if (err.response?.status === 401) {
        setError(language === 'en' ? 'Session expired - Please reconnect' : 'Session expirée - Veuillez vous reconnecter');
      } else {
        const errorMessage = err.response?.data?.detail || err.message || t('errorLoading');
        setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCustomsClearSingle = async () => {
    setStatusChanging(true);
    try {
      await api.put(`/api/packages/${customsDialog.packageId}/mark-customs-cleared`, null, {
        params: { note: customsDialog.note.trim() || undefined }
      });
      setCustomsDialog({ open: false, packageId: null, oceCode: '', note: '' });
      toast({ title: 'Dédouané', description: `Colis ${customsDialog.oceCode} dédouané avec succès` });
      fetchData();
      if (selectedShipment) fetchShipmentDetails(selectedShipment);
    } catch (err) {
      toast({ title: t('error'), description: err.response?.data?.detail || t('errorCustomsClearance'), variant: 'destructive' });
    } finally {
      setStatusChanging(false);
    }
  };

  const handleCustomsClearBulk = async () => {
    setStatusChanging(true);
    let successCount = 0;
    let failCount = 0;
    for (const pkgId of customsBulkDialog.packageIds) {
      try {
        await api.put(`/api/packages/${pkgId}/mark-customs-cleared`, null, {
          params: { note: customsBulkDialog.note.trim() || undefined }
        });
        successCount++;
      } catch {
        failCount++;
      }
    }
    setCustomsBulkDialog({ open: false, shipmentId: null, shipmentName: '', packageIds: [], note: '' });
    toast({
      title: 'Dédouanement groupé',
      description: `${successCount} colis dédouané(s)${failCount ? `, ${failCount} échec(s)` : ''}`,
      variant: failCount ? 'destructive' : 'default'
    });
    fetchData();
    if (selectedShipment) fetchShipmentDetails(selectedShipment);
    setStatusChanging(false);
  };

  const handlePackageStatusChange = async () => {
    if (!packageStatusDialog.newStatus || !packageStatusDialog.note.trim()) return;
    setStatusChanging(true);
    try {
      await api.put(`/api/packages/${packageStatusDialog.packageId}/status`, {
        status: packageStatusDialog.newStatus,
        note: packageStatusDialog.note.trim()
      });
      setPackageStatusDialog(prev => ({ ...prev, open: false }));
      toast({ title: language === 'en' ? 'Status changed' : 'Statut modifié', description: `${language === 'en' ? 'Package' : 'Colis'} ${packageStatusDialog.oceCode} → ${(STATUS_LABELS[language] || STATUS_LABELS.fr)[packageStatusDialog.newStatus] || packageStatusDialog.newStatus}` });
      fetchData();
      if (selectedShipment) fetchShipmentDetails(selectedShipment);
    } catch (err) {
      toast({ title: t('error'), description: err.response?.data?.detail || t('errorStatusChange'), variant: 'destructive' });
    } finally {
      setStatusChanging(false);
    }
  };

  const handleShipmentStatusChange = async () => {
    if (!shipmentStatusDialog.newStatus) return;
    setStatusChanging(true);
    try {
      await api.put(`/api/shipments/${shipmentStatusDialog.shipmentId}`, {
        status: shipmentStatusDialog.newStatus,
        status_change_note: shipmentStatusDialog.note.trim() || undefined
      });
      setShipmentStatusDialog(prev => ({ ...prev, open: false }));
      toast({ title: language === 'en' ? 'Departure status changed' : 'Statut départ modifié', description: `${shipmentStatusDialog.shipmentName} → ${(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[shipmentStatusDialog.newStatus] || shipmentStatusDialog.newStatus}` });
      fetchData();
    } catch (err) {
      toast({ title: t('error'), description: err.response?.data?.detail || t('errorStatusChange'), variant: 'destructive' });
    } finally {
      setStatusChanging(false);
    }
  };

  const handleVerifyFlight = async (shipmentId, shipmentName) => {
    setVerifyingFlight(true);
    try {
      const res = await api.post(`/api/shipments/${shipmentId}/verify-flight`);
      const data = res.data;
      if (data.verified) {
        toast({
          title: language === 'en' ? 'Flight verified' : 'Vol vérifié',
          description: data.message + (data.flight_data?.airline ? ` (${data.flight_data.airline})` : '')
        });
      } else {
        toast({
          title: language === 'en' ? 'Flight not found' : 'Vol non trouvé',
          description: data.message,
          variant: 'destructive'
        });
      }
      fetchData();
      if (selectedShipment) fetchShipmentDetails({ ...selectedShipment, id: shipmentId });
    } catch (err) {
      toast({
        title: language === 'en' ? 'Verification error' : 'Erreur de vérification',
        description: err.response?.data?.detail || err.message,
        variant: 'destructive'
      });
    } finally {
      setVerifyingFlight(false);
    }
  };

  const getFlightradarUrl = (flightName) => {
    if (!flightName) return null;
    const clean = flightName.replace(/[\s-]/g, '').toUpperCase();
    return `https://www.flightradar24.com/data/flights/${clean}`;
  };

  const FlightVerificationBadge = ({ shipment }) => {
    if (!shipment.flight_name) {
      return (
        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-xs gap-1">
          <XCircle className="h-3 w-3" />
          {language === 'en' ? 'No tracking' : 'Tracking manquant'}
        </Badge>
      );
    }
    if (shipment.flight_verified) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs gap-1">
          <CheckCircle2 className="h-3 w-3" />
          {language === 'en' ? 'Verified' : 'Vérifié'}
          {shipment.flight_verification_date && (
            <span className="opacity-60">
              ({new Date(shipment.flight_verification_date).toLocaleDateString('fr-FR')})
            </span>
          )}
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs gap-1">
        <AlertTriangle className="h-3 w-3" />
        {language === 'en' ? 'Not verified' : 'Non vérifié'}
      </Badge>
    );
  };

  const handleCreateShipment = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newShipment.departure_date && newShipment.expected_arrival_date
        && new Date(newShipment.departure_date) > new Date(newShipment.expected_arrival_date)) {
      const msg = language === 'en'
        ? 'Departure date cannot be after the expected arrival date.'
        : "La date de départ ne peut pas être postérieure à la date d'arrivée prévue.";
      toast({ title: t('error'), description: msg, variant: 'destructive' });
      return;
    }

    try {
      const shipmentData = {
        ...newShipment,
        transport_mode: selectedTransportMode,
        departure_date: newShipment.departure_date ? new Date(newShipment.departure_date).toISOString() : null,
        expected_arrival_date: newShipment.expected_arrival_date ? new Date(newShipment.expected_arrival_date).toISOString() : null,
        deposit_deadline: newShipment.deposit_deadline ? new Date(newShipment.deposit_deadline).toISOString() : null
      };

      await api.post('/api/shipments', shipmentData);
      
      setSuccess('Départ créé avec succès !');
      setNewShipment({
        name: '',
        flight_name: '',
        origin_country: '',
        destination_country: '',
        departure_date: '',
        expected_arrival_date: '',
        deposit_deadline: '',
        notes: '',
        transport_mode: ''
      });
      setSelectedTransportMode(defaultTransportMode);
      setSelectedDestinationCountry('');
      setSelectedDestinationCity('');
      setOpenCreateDialog(false);
      fetchData();
    } catch (err) {
      toast({ title: t('error'), description: err.response?.data?.detail || t('errorCreating'), variant: 'destructive' });
    }
  };

  const handleAssignToShipment = async (shipmentId) => {
    if (selectedPackages.size === 0) {
      setError('Veuillez sélectionner au moins un colis');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await api.post(
        '/api/shipments/bulk-add-packages',
        {
          shipment_id: shipmentId,
          package_ids: Array.from(selectedPackages)
        }
      );
      
      const { collected_count, partner_summary, draft_warning } = response.data;

      // Show summary
      let message = `✅ ${collected_count} colis ajoutés au départ\n\n`;
      Object.values(partner_summary).forEach(info => {
        if (info.missing && info.missing.length > 0) {
          message += `⚠️ ${info.missing.length} colis manquants signalés\n`;
        }
      });
      if (draft_warning) {
        message += `\n⚠️ ${draft_warning.count} brouillon(s) non validé(s) ignoré(s) : ${draft_warning.oce_codes.join(', ')}`;
      }
      
      setSuccess(message);
      setSelectedPackages(new Set());
      setOpenAssignDialog(false);
      fetchData();
    } catch (err) {
      console.error('Error assigning packages:', err);
      const errorMsg = err.response?.data?.detail || err.message || t('errorAssigning');
      setError(typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg));
    }
  };

  const togglePackageSelection = (packageId) => {
    const newSet = new Set(selectedPackages);
    if (newSet.has(packageId)) {
      newSet.delete(packageId);
    } else {
      newSet.add(packageId);
    }
    setSelectedPackages(newSet);
  };

  const togglePartnerSelection = (packages) => {
    const packageIds = packages.map(p => p.id);
    const allSelected = packageIds.every(id => selectedPackages.has(id));
    
    const newSet = new Set(selectedPackages);
    if (allSelected) {
      packageIds.forEach(id => newSet.delete(id));
    } else {
      packageIds.forEach(id => newSet.add(id));
    }
    setSelectedPackages(newSet);
  };

  const openEditShipment = (shipment) => {
    const toLocalDatetime = (iso) => {
      if (!iso) return '';
      const d = new Date(iso);
      const offset = d.getTimezoneOffset();
      const local = new Date(d.getTime() - offset * 60000);
      return local.toISOString().slice(0, 16);
    };
    setSelectedShipment(shipment);
    setEditShipment({
      name: shipment.name || '',
      flight_name: shipment.flight_name || '',
      origin_country: shipment.origin_country || '',
      destination_country: shipment.destination_country || '',
      departure_date: toLocalDatetime(shipment.departure_date),
      expected_arrival_date: toLocalDatetime(shipment.expected_arrival_date),
      deposit_deadline: toLocalDatetime(shipment.deposit_deadline),
      notes: shipment.notes || ''
    });
    setOpenEditDialog(true);
  };

  const handleUpdateShipment = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (editShipment.departure_date && editShipment.expected_arrival_date
        && new Date(editShipment.departure_date) > new Date(editShipment.expected_arrival_date)) {
      const msg = language === 'en'
        ? 'Departure date cannot be after the expected arrival date.'
        : "La date de départ ne peut pas être postérieure à la date d'arrivée prévue.";
      setError(msg);
      toast({ title: t('error'), description: msg, variant: 'destructive' });
      return;
    }

    try {
      const updateData = {};
      if (editShipment.name) updateData.name = editShipment.name;
      if (editShipment.flight_name) updateData.flight_name = editShipment.flight_name;
      if (editShipment.origin_country) updateData.origin_country = editShipment.origin_country;
      if (editShipment.destination_country) updateData.destination_country = editShipment.destination_country;
      if (editShipment.departure_date) updateData.departure_date = new Date(editShipment.departure_date).toISOString();
      if (editShipment.expected_arrival_date) updateData.expected_arrival_date = new Date(editShipment.expected_arrival_date).toISOString();
      if (editShipment.deposit_deadline) updateData.deposit_deadline = new Date(editShipment.deposit_deadline).toISOString();
      if (editShipment.notes !== undefined) updateData.notes = editShipment.notes;

      const res = await api.put(`/api/shipments/${selectedShipment.id}`, updateData);

      const desc = res.data?.flight_warning
        ? `"${editShipment.name}" mis à jour. ⚠️ ${res.data.flight_warning}`
        : `"${editShipment.name}" mis à jour avec succès`;
      toast({ title: 'Départ modifié', description: desc, variant: res.data?.flight_warning ? 'destructive' : 'default' });
      setOpenEditDialog(false);
      setSelectedShipment(null);
      fetchData();
    } catch (err) {
      console.error('Error updating shipment:', err);
      const errorMsg = err.response?.data?.detail || err.message || t('errorUpdating');
      toast({ title: t('error'), description: typeof errorMsg === 'string' ? errorMsg : JSON.stringify(errorMsg), variant: 'destructive' });
    }
  };

  const fetchShipmentDetails = async (shipment) => {
    setLoadingDetails(true);
    setSelectedShipment(shipment);
    setOpenDetailsDialog(true);

    try {
      const response = await api.get(`/api/shipments/${shipment.id}/packages`);
      setShipmentDetails(response.data);
    } catch (err) {
      console.error('Error fetching shipment details:', err);
      setError(t('errorLoading'));
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleRemoveFromShipment = async (pkg) => {
    if (!window.confirm(language === 'en'
      ? `Remove ${pkg.oce_code} from this departure? It will return to the collector's packages.`
      : `Retirer ${pkg.oce_code} de ce départ ? Il reviendra chez le collecteur (Mes colis).`)) return;
    try {
      await api.put(`/api/packages/${pkg.id}/remove-from-shipment`);
      toast({ title: language === 'en' ? 'Package removed' : 'Colis retiré', description: `${pkg.oce_code} → ${language === 'en' ? 'My packages' : 'Mes colis'}` });
      if (selectedShipment) fetchShipmentDetails(selectedShipment);
      fetchData();
    } catch (err) {
      toast({ title: t('error'), description: err.response?.data?.detail || t('errorStatusChange'), variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <ProfessionalLayout>
        <p>{t('loading')}</p>
      </ProfessionalLayout>
    );
  }

  const totalPendingPackages = groupedPackages.reduce((sum, g) => sum + g.package_count, 0);
  const allPendingPkgs = groupedPackages.flatMap(g => g.packages);
  const totalPendingWeight = allPendingPkgs.reduce((sum, p) => sum + (p.weight || 0), 0);
  const totalPendingPrice = allPendingPkgs.reduce((sum, p) => sum + (p.total_price || p.price || 0), 0);

  // Séparation départs actifs / historique (terminés ou annulés)
  // Départs actifs triés par date de départ la plus proche d'abord (sans date → à la fin).
  const activeShipments = shipments
    .filter(s => !HISTORY_STATUSES.includes(s.status))
    .sort((a, b) => {
      const da = a.departure_date ? new Date(a.departure_date).getTime() : Infinity;
      const db2 = b.departure_date ? new Date(b.departure_date).getTime() : Infinity;
      return da - db2;
    });
  const historyShipments = shipments.filter(s => HISTORY_STATUSES.includes(s.status));

  // Carte d'un départ, réutilisée à l'identique par l'onglet Départs et l'onglet Historique
  const renderShipmentCard = (shipment) => {
    const sc = shipment.status_counts || {};
    const delivered = sc.delivered || 0;
    const pkgCount = shipment.package_count || 0;
    const progressPct = pkgCount > 0 ? Math.round((delivered / pkgCount) * 100) : 0;
    const awaitingCustoms = sc.awaiting_customs || 0;
    const isFinished = shipment.status === 'completed' || shipment.status === 'cancelled';

    return (
                        <div
                          key={shipment.id}
                          className={`p-4 border border-border rounded-lg hover:bg-muted cursor-pointer transition-all ${isFinished ? 'opacity-60' : ''}`}
                          onClick={() => fetchShipmentDetails(shipment)}
                        >
                          {/* Line 1: Name + Badge + ETA */}
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Ship className="h-5 w-5 text-muted-foreground shrink-0" />
                              <p className="font-medium text-base sm:text-lg">{shipment.name}</p>
                              <Badge className={
                                shipment.status === 'completed' ? 'bg-green-500' :
                                shipment.status === 'delivering' ? 'bg-blue-600' :
                                shipment.status === 'awaiting_delivery' ? 'bg-amber-500' :
                                shipment.status === 'in_progress' || shipment.status === 'in_transit' ? 'bg-accent' :
                                shipment.status === 'cancelled' ? 'bg-red-500' :
                                shipment.status === 'arrived' ? 'bg-green-600' :
                                shipment.status === 'delayed' ? 'bg-amber-500' : 'bg-muted-foreground'
                              }>
                                {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[shipment.status] || shipment.status}
                              </Badge>
                              {trackingMode === 'automatic' && shipment.flight_name && !shipment.manual_status_override && (
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                  <Zap className="h-3 w-3 mr-1" />{t('trackingModeBadgeAuto')}
                                </Badge>
                              )}
                              {shipment.manual_status_override && (
                                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                  <Pencil className="h-3 w-3 mr-1" />{t('trackingModeBadgeManual')}
                                </Badge>
                              )}
                              {(() => {
                                const eta = getShipmentETA(shipment);
                                if (!eta) return null;
                                const colorMap = { blue: 'bg-accent/10 text-accent', orange: 'bg-amber-100 text-amber-800', red: 'bg-red-100 text-red-800' };
                                return (
                                  <Badge variant="outline" className={`text-xs ${colorMap[eta.color] || ''}`}>
                                    {eta.text}
                                  </Badge>
                                );
                              })()}
                            </div>
                            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                              {(['planned', 'scheduled'].includes(shipment.status)
                                || (canEditInTransit && !HISTORY_STATUSES.includes(shipment.status))) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditShipment(shipment);
                                  }}
                                >
                                  <Pencil className="h-4 w-4 sm:mr-1" />
                                  <span className="hidden sm:inline">{t('edit')}</span>
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBroadcastDialog({ open: true, shipmentId: shipment.id, shipmentName: shipment.name });
                                }}
                              >
                                <Megaphone className="h-4 w-4 sm:mr-1" />
                                <span className="hidden sm:inline">{language === 'en' ? 'Broadcast' : 'Diffuser'}</span>
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchShipmentDetails(shipment);
                                }}
                              >
                                <Eye className="h-4 w-4 sm:mr-1" />
                                <span className="hidden sm:inline">{language === 'en' ? 'Details' : 'Détails'}</span>
                              </Button>
                              {SHIPMENT_TRANSITIONS[shipment.status]?.length > 0 && (
                                <div onClick={(e) => e.stopPropagation()}>
                                  <Select onValueChange={(newStatus) => {
                                    setShipmentStatusDialog({
                                      open: true,
                                      shipmentId: shipment.id,
                                      shipmentName: shipment.name,
                                      currentStatus: shipment.status,
                                      newStatus,
                                      note: ''
                                    });
                                  }}>
                                    <SelectTrigger className="h-8 w-auto min-w-[120px] text-xs">
                                      <RefreshCw className="h-3 w-3 mr-1" />
                                      <span>{t('statusChangeInline')}</span>
                                    </SelectTrigger>
                                    <SelectContent>
                                      {SHIPMENT_TRANSITIONS[shipment.status].map(s => (
                                        <SelectItem key={s} value={s}>
                                          {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[s] || s}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Line 2: Route + Vol + Dates */}
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-2">
                            <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                            {shipment.transport_mode && (
                              <Badge className={`${TRANSPORT_MODE_COLORS[shipment.transport_mode] || 'bg-blue-500 text-white'} text-xs`}>
                                {shipment.transport_mode === 'aerien' ? <Plane className="h-3 w-3 mr-1" /> : <Ship className="h-3 w-3 mr-1" />}
                                {(TRANSPORT_MODE_LABELS[language] || TRANSPORT_MODE_LABELS.fr)[shipment.transport_mode] || shipment.transport_mode}
                              </Badge>
                            )}
                            {shipment.flight_name && (
                              <span className="text-accent flex items-center gap-1">
                                {shipment.transport_mode === 'maritime' ? <Ship className="h-3.5 w-3.5" /> : <Plane className="h-3.5 w-3.5" />}
                                {shipment.flight_name}
                              </span>
                            )}
                            <FlightVerificationBadge shipment={shipment} />
                            {shipment.flight_name && shipment.transport_mode !== 'maritime' && (
                              <a
                                href={getFlightradarUrl(shipment.flight_name)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Flightradar24
                              </a>
                            )}
                            {shipment.flight_name && !shipment.flight_verified && ['planned', 'scheduled'].includes(shipment.status) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                disabled={verifyingFlight}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVerifyFlight(shipment.id, shipment.name);
                                }}
                              >
                                {verifyingFlight ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                                {language === 'en' ? 'Verify' : 'Vérifier'}
                              </Button>
                            )}
                            {shipment.departure_date && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                Départ: {new Date(shipment.departure_date).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                            {shipment.expected_arrival_date && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                Arrivée prév.: {new Date(shipment.expected_arrival_date).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                            {shipment.deposit_deadline && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground/60">
                                <Clock className="h-3 w-3" />
                                Limite dépôt: {new Date(shipment.deposit_deadline).toLocaleDateString('fr-FR')}
                              </span>
                            )}
                          </div>

                          {/* Line 3: Progress bar + counters + customs */}
                          <div className="flex items-center gap-3 mt-3">
                            <div className="flex-1">
                              <Progress
                                value={progressPct}
                                className="h-2"
                                indicatorClassName={progressPct >= 100 ? 'bg-green-500' : progressPct > 0 ? 'bg-accent' : ''}
                              />
                            </div>
                            <span className={`text-sm font-medium whitespace-nowrap ${progressPct >= 100 ? 'text-green-600' : 'text-foreground'}`}>
                              {delivered}/{pkgCount} {language === 'en' ? 'delivered' : 'livrés'}
                            </span>
                            {awaitingCustoms > 0 && (
                              <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20 text-xs">
                                {language === 'en' ? `${awaitingCustoms} to clear` : `${awaitingCustoms} à dédouaner`}
                              </Badge>
                            )}
                          </div>
                        </div>
    );
  };

  return (
    <ProfessionalLayout>
      <SEOHead title={t('seoDashboardTitle')} description={t('seoDashboardDesc')} noindex />
      <div className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert className="bg-green-50 border-green-200">
            <AlertDescription className="text-green-800 whitespace-pre-line">{success}</AlertDescription>
          </Alert>
        )}

        {/* Customs Alert Banner */}
        {totalAwaitingCustoms > 0 && (
          <Alert className="bg-amber-50 border-amber-300">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <AlertDescription className="text-amber-800 font-medium">
              {language === 'en' ? `${totalAwaitingCustoms} packages awaiting customs clearance — Action required` : `${totalAwaitingCustoms} colis en attente de dédouanement — Action requise`}
            </AlertDescription>
          </Alert>
        )}

        {/* Statistics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8d6 100%)', borderColor: '#f0c8a8' }}>
            <div className="analytics-kpi-dot" style={{ background: '#e8590c' }} />
            <span className="analytics-kpi-label">{language === 'en' ? 'Pending Packages' : 'Colis en Attente'}</span>
            <span className="analytics-kpi-value">{totalPendingPackages}</span>
            <span className="analytics-kpi-delta">{language === 'en' ? 'Not assigned' : 'Non assignés'}</span>
          </div>

          <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #e8ecf4 100%)', borderColor: '#c7d2e8' }}>
            <div className="analytics-kpi-dot" style={{ background: '#0ea5e9' }} />
            <span className="analytics-kpi-label">{language === 'en' ? 'Total Weight' : 'Poids Total'}</span>
            <span className="analytics-kpi-value">{totalPendingWeight.toFixed(1)} kg</span>
            <span className="analytics-kpi-delta">{language === 'en' ? 'All pending packages' : 'Tous les colis'}</span>
          </div>

          <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' }}>
            <div className="analytics-kpi-dot" style={{ background: '#22c55e' }} />
            <span className="analytics-kpi-label">{language === 'en' ? 'Total Price' : 'Prix Total'}</span>
            <span className="analytics-kpi-value">{totalPendingPrice.toLocaleString('fr-FR')} €</span>
            <span className="analytics-kpi-delta">{language === 'en' ? 'Revenue pending' : 'CA en attente'}</span>
          </div>

          <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', borderColor: '#cbd5e1' }}>
            <div className="analytics-kpi-dot" style={{ background: '#1a1a1a' }} />
            <span className="analytics-kpi-label">{language === 'en' ? 'Partners' : 'Partenaires'}</span>
            <span className="analytics-kpi-value">{groupedPackages.length}</span>
            <span className="analytics-kpi-delta">{language === 'en' ? 'With pending packages' : 'Avec colis en attente'}</span>
          </div>

          <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0e4ff 100%)', borderColor: '#d4b8f0' }}>
            <div className="analytics-kpi-dot" style={{ background: '#a855f7' }} />
            <span className="analytics-kpi-label">{language === 'en' ? 'Selected' : 'Sélectionnés'}</span>
            <span className="analytics-kpi-value">{selectedPackages.size}</span>
            <span className="analytics-kpi-delta">{language === 'en' ? 'Selected packages' : 'Colis sélectionnés'}</span>
          </div>
        </div>

        {/* Barre de recherche */}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder={language === 'en' ? 'Filter pending packages / departures...' : 'Filtrer colis en attente / départs...'}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
                setPackagePageByPartner({});
              }}
              className="pl-10"
            />
          </div>
          <Button variant="outline" className="shrink-0" onClick={() => setOpenGlobalSearch(true)}>
            <Search className="h-4 w-4 mr-2" />
            {t('globalSearch')}
          </Button>
          <Button variant="outline" className="shrink-0" onClick={copyPublicCalendarLink} title={publicCalendarUrl}>
            <Calendar className="h-4 w-4 mr-2" />
            {language === 'en' ? 'Public calendar' : 'Calendrier public'}
          </Button>
        </div>

        <PackageSearch open={openGlobalSearch} onOpenChange={setOpenGlobalSearch} />

        <Tabs defaultValue="packages" className="w-full" onValueChange={setActiveMainTab}>
          <TabsList>
            <TabsTrigger value="packages">{language === 'en' ? 'Packages by Partner' : 'Colis par Partenaire'}</TabsTrigger>
            <TabsTrigger value="shipments">{language === 'en' ? 'Departures' : 'Départs'} ({activeShipments.length})</TabsTrigger>
            <TabsTrigger value="history">
              {language === 'en' ? 'History' : 'Historique'}
              {historyShipments.length > 0 && (
                <Badge variant="secondary" className="ml-2 h-5 px-1.5 text-xs">{historyShipments.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="packages" className="space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <h2 className="text-lg sm:text-xl font-semibold">{language === 'en' ? 'Packages to Collect' : 'Colis à Collecter'}</h2>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                {loadingMode === 'auto' && (
                  <Button variant="outline" className="flex-1 sm:flex-none min-w-0" onClick={() => setStartLoadingOpen(true)}>
                    <PackageCheck className="h-4 w-4 mr-2 shrink-0" />
                    <span className="truncate">{language === 'en' ? 'Start loading' : 'Démarrer un chargement'}</span>
                  </Button>
                )}
                <Dialog open={openCreateDialog} onOpenChange={setOpenCreateDialog}>
                  <DialogTrigger asChild>
                    <Button className="flex-1 sm:flex-none min-w-0" disabled={totalPendingPackages === 0} title={totalPendingPackages === 0 ? t('noPackagesAvailable') : ''}>
                      <Ship className="h-4 w-4 mr-2 shrink-0" />
                      <span className="truncate">{language === 'en' ? 'New Departure' : 'Nouveau Départ'}</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <form onSubmit={handleCreateShipment}>
                      <DialogHeader>
                        <DialogTitle>{language === 'en' ? 'Create a Departure' : 'Créer un Départ'}</DialogTitle>
                        <DialogDescription>
                          {language === 'en' ? 'Create a new trip to group packages' : 'Créez un nouveau voyage pour grouper les colis'}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>{language === 'en' ? 'Departure name' : 'Nom du départ'}</Label>
                          <Input
                            value={newShipment.name}
                            onChange={(e) => setNewShipment({ ...newShipment, name: e.target.value })}
                            placeholder={language === 'en' ? 'Departure Paris-New York' : 'Départ Paris-New York'}
                            required
                          />
                        </div>
                        
                        {/* Transport mode selector */}
                        {transportModes.length > 1 && (
                          <div className="space-y-2">
                            <Label>{t('transportMode')}</Label>
                            <div className="flex flex-wrap gap-2 sm:gap-3">
                              {transportModes.map(mode => (
                                <button
                                  key={mode}
                                  type="button"
                                  onClick={() => setSelectedTransportMode(mode)}
                                  className={`flex items-center gap-2 px-3 py-2 sm:px-4 rounded-md border-2 transition-all text-sm ${
                                    selectedTransportMode === mode
                                      ? 'border-accent bg-accent/10 text-accent'
                                      : 'border-slate-200 text-muted-foreground hover:border-slate-300'
                                  }`}
                                >
                                  {mode === 'aerien' ? <Plane className="h-4 w-4" /> : <Ship className="h-4 w-4" />}
                                  {(TRANSPORT_MODE_LABELS[language] || TRANSPORT_MODE_LABELS.fr)[mode]}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        <div className="space-y-2">
                          <Label>{selectedTransportMode === 'maritime' ? (language === 'en' ? 'Ship name / Reference' : 'Nom du navire / Référence') : (language === 'en' ? 'Flight name / Main tracking number' : 'Nom du vol / Numéro de tracking principal')}</Label>
                          <Input
                            value={newShipment.flight_name}
                            onChange={(e) => setNewShipment({ ...newShipment, flight_name: e.target.value })}
                            placeholder={selectedTransportMode === 'maritime' ? 'MSC OSCAR ou MAEU-123456' : 'AF001 ou FLIGHT-001'}
                          />
                          <p className="text-xs text-muted-foreground">
                            {selectedTransportMode === 'maritime'
                              ? (language === 'en' ? 'Ship reference or container number' : 'Référence du navire ou numéro de conteneur')
                              : (language === 'en' ? 'This name will be used as the main tracking reference' : 'Ce nom servira de référence principale pour le suivi automatique')}
                          </p>
                          {!newShipment.flight_name && (
                            <p className="text-xs text-amber-600 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {language === 'en'
                                ? 'You can add the flight number later. A reminder will be sent.'
                                : 'Vous pourrez ajouter le numéro de vol plus tard. Un rappel sera envoyé.'}
                            </p>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <Label>{t('originCountry')}</Label>
                          <SearchableSelect
                            value={newShipment.origin_country}
                            onChange={(val) => setNewShipment({ ...newShipment, origin_country: val })}
                            required
                            placeholder={t('selectACountry') || 'Sélectionner un pays'}
                            searchPlaceholder={t('search') || 'Rechercher un pays…'}
                            options={DEFAULT_COUNTRIES.map((c) => ({
                              value: c.name,
                              label: `${getFlagEmoji(c.code)} ${c.name}`,
                            }))}
                          />
                        </div>

                        <GeographicSelector
                          selectedCountry={selectedDestinationCountry}
                          selectedCity={selectedDestinationCity}
                          onCountryChange={(country) => {
                            setSelectedDestinationCountry(country);
                            setNewShipment({ ...newShipment, destination_country: country });
                          }}
                          onCityChange={setSelectedDestinationCity}
                          required={true}
                        />
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2 min-w-0">
                            <Label>{t('departureDate')}</Label>
                            <Input
                              type="datetime-local"
                              className="w-full min-w-0"
                              value={newShipment.departure_date}
                              onChange={(e) => setNewShipment((s) => ({
                                ...s,
                                departure_date: e.target.value,
                                deposit_deadline: s.deposit_deadline || depositDeadlineFromDeparture(e.target.value),
                              }))}
                            />
                          </div>

                          <div className="space-y-2 min-w-0">
                            <Label>{language === 'en' ? 'Expected arrival date *' : 'Date d\'arrivée prévue *'}</Label>
                            <Input
                              type="datetime-local"
                              className="w-full min-w-0"
                              value={newShipment.expected_arrival_date}
                              onChange={(e) => setNewShipment({ ...newShipment, expected_arrival_date: e.target.value })}
                              required
                            />
                            <p className="text-xs text-muted-foreground">
                              {language === 'en' ? 'Used for automatic checks' : 'Utilisée pour les vérifications automatiques'}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>{language === 'en' ? 'Package deposit deadline (optional)' : 'Date limite de dépôt des colis (optionnel)'}</Label>
                          <Input
                            type="datetime-local"
                            className="w-full min-w-0"
                            value={newShipment.deposit_deadline}
                            onChange={(e) => setNewShipment({ ...newShipment, deposit_deadline: e.target.value })}
                          />
                          <p className="text-xs text-muted-foreground">
                            {language === 'en' ? 'Shown on the public calendar' : 'Affichée sur le calendrier public'}
                          </p>
                        </div>

                        <div className="space-y-2">
                          <Label>{language === 'en' ? 'Notes (optional)' : 'Notes (optionnel)'}</Label>
                          <Input
                            value={newShipment.notes}
                            onChange={(e) => setNewShipment({ ...newShipment, notes: e.target.value })}
                            placeholder={language === 'en' ? 'Additional flight information' : 'Informations supplémentaires sur le vol'}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="submit">{language === 'en' ? 'Create Departure' : 'Créer le Départ'}</Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                {loadingMode === 'manual' && selectedPackages.size > 0 && (
                  <Dialog open={openAssignDialog} onOpenChange={setOpenAssignDialog}>
                    <DialogTrigger asChild>
                      <Button variant="outline">
                        {language === 'en' ? `Assign to a Departure (${selectedPackages.size})` : `Assigner à un Départ (${selectedPackages.size})`}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{language === 'en' ? 'Assign selected packages' : 'Assigner les colis sélectionnés'}</DialogTitle>
                        <DialogDescription>
                          {language === 'en' ? `Choose a departure for ${selectedPackages.size} packages` : `Choisissez un départ pour ${selectedPackages.size} colis`}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-3 max-h-96 overflow-y-auto py-4">
                        {(() => {
                          const byDeparture = (a, b) => {
                            const da = a.departure_date ? new Date(a.departure_date).getTime() : Infinity;
                            const db2 = b.departure_date ? new Date(b.departure_date).getTime() : Infinity;
                            return da - db2;
                          };
                          const availableShipments = shipments.filter(s => ['planned', 'scheduled'].includes(s.status)).sort(byDeparture);
                          const unavailableShipments = shipments.filter(s => !['planned', 'scheduled'].includes(s.status)).sort(byDeparture);

                          return (
                            <>
                              {availableShipments.length > 0 ? (
                                <>
                                  {availableShipments.map(shipment => (
                                    <div
                                      key={shipment.id}
                                      className="p-4 border-2 border-green-200 rounded-lg hover:bg-green-50 cursor-pointer"
                                      onClick={() => setConfirmAssign({ shipmentId: shipment.id, shipmentName: shipment.name })}
                                    >
                                      <div className="flex justify-between items-start">
                                        <div>
                                          <p className="font-medium">{shipment.name}</p>
                                          <div className="text-sm text-muted-foreground">
                                            <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                                          </div>
                                          <p className="text-xs text-muted-foreground">
                                            {shipment.package_count || 0} {language === 'en' ? 'packages currently' : 'colis actuellement'}
                                          </p>
                                        </div>
                                        <Badge className="bg-green-500">{language === 'en' ? 'Available' : 'Disponible'}</Badge>
                                      </div>
                                    </div>
                                  ))}
                                  
                                  {unavailableShipments.length > 0 && (
                                    <>
                                      <div className="pt-4 border-t">
                                        <p className="text-sm text-muted-foreground mb-3">
                                          {language === 'en' ? '⚠️ Unavailable departures (in transit, completed or cancelled):' : '⚠️ Départs non disponibles (en transit, terminés ou annulés) :'}
                                        </p>
                                      </div>
                                      {unavailableShipments.map(shipment => (
                                        <div
                                          key={shipment.id}
                                          className="p-4 border border-gray-300 rounded-lg bg-muted opacity-60 cursor-not-allowed"
                                        >
                                          <div className="flex justify-between items-start">
                                            <div>
                                              <p className="font-medium text-muted-foreground">{shipment.name}</p>
                                              <p className="text-sm text-muted-foreground">
                                                <CountryRoute origin={shipment.origin_country} destination={shipment.destination_country} size="sm" />
                                              </p>
                                              <p className="text-xs text-muted-foreground">
                                                {shipment.package_count || 0} {language === 'en' ? 'packages currently' : 'colis actuellement'}
                                              </p>
                                            </div>
                                            <Badge variant="outline" className={
                                              shipment.status === 'in_progress' || shipment.status === 'in_transit' ? 'bg-accent/10 text-accent' :
                                              shipment.status === 'completed' ? 'bg-muted text-foreground' :
                                              shipment.status === 'arrived' ? 'bg-green-100 text-green-700' :
                                              shipment.status === 'delayed' ? 'bg-amber-100 text-amber-700' :
                                              'bg-red-100 text-red-700'
                                            }>
                                              {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[shipment.status] || shipment.status}
                                            </Badge>
                                          </div>
                                        </div>
                                      ))}
                                    </>
                                  )}
                                </>
                              ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                  <p>{t('noShipmentsAvailable')}</p>
                                  <p className="text-sm mt-2">
                                    {unavailableShipments.length > 0
                                      ? (language === 'en' ? "All departures are in transit, completed or cancelled." : "Tous les départs sont en transit, terminés ou annulés.")
                                      : (language === 'en' ? "Create a scheduled departure first." : "Créez d'abord un départ planifié.")}
                                  </p>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {/* Confirmation dialog for assignment */}
                <Dialog open={!!confirmAssign} onOpenChange={(open) => !open && setConfirmAssign(null)}>
                  <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>{language === 'en' ? 'Confirm assignment' : 'Confirmer l\'assignation'}</DialogTitle>
                      <DialogDescription>
                        {language === 'en'
                          ? `Assign ${selectedPackages.size} package(s) to "${confirmAssign?.shipmentName}"?`
                          : `Assigner ${selectedPackages.size} colis au départ "${confirmAssign?.shipmentName}" ?`}
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="flex gap-2 sm:gap-0">
                      <Button variant="outline" onClick={() => setConfirmAssign(null)}>
                        {t('cancel')}
                      </Button>
                      <Button onClick={() => {
                        const shipmentId = confirmAssign.shipmentId;
                        setConfirmAssign(null);
                        handleAssignToShipment(shipmentId);
                      }}>
                        {language === 'en' ? 'Confirm' : 'Confirmer'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {groupedPackages.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <EmptyState
                    icon={Package}
                    title={t('noPendingPackages')}
                    description={language === 'en' ? 'All packages have been collected or no packages registered yet' : 'Tous les colis ont été collectés ou il n\'y a pas encore de colis enregistrés'}
                  />
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {groupedPackages.map((group) => {
                  const query = searchQuery.toLowerCase().trim();
                  const filteredPkgs = query
                    ? group.packages.filter(pkg => {
                        return (
                          pkg.oce_code?.toLowerCase().includes(query) ||
                          pkg.sender_name?.toLowerCase().includes(query) ||
                          pkg.recipient_name?.toLowerCase().includes(query) ||
                          pkg.sender_phone?.includes(query) ||
                          pkg.recipient_phone?.includes(query) ||
                          pkg.origin_country?.toLowerCase().includes(query) ||
                          pkg.destination_country?.toLowerCase().includes(query) ||
                          pkg.destination_city?.toLowerCase().includes(query) ||
                          pkg.description?.toLowerCase().includes(query)
                        );
                      })
                    : group.packages;

                  // Also match on partner name/email
                  const partnerMatch = query && (
                    group.partner_name?.toLowerCase().includes(query) ||
                    group.partner_email?.toLowerCase().includes(query)
                  );

                  const visiblePkgs = partnerMatch ? group.packages : filteredPkgs;
                  if (query && visiblePkgs.length === 0) return null;

                  const allSelected = visiblePkgs.every(p => selectedPackages.has(p.id));
                  const someSelected = visiblePkgs.some(p => selectedPackages.has(p.id));
                  
                  return (
                    <Card key={group.partner_id}>
                      <CardHeader>
                        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                          <div className="flex items-center gap-3 min-w-0">
                            {loadingMode === 'manual' && (
                              <Checkbox
                                checked={allSelected}
                                onCheckedChange={() => togglePartnerSelection(visiblePkgs)}
                                className={`shrink-0 ${someSelected && !allSelected ? 'opacity-50' : ''}`}
                              />
                            )}
                            {group.partner_avatar ? (
                              <img
                                src={group.partner_avatar}
                                alt=""
                                className="shrink-0 h-9 w-9 rounded-full object-cover border border-border cursor-zoom-in hover:ring-2 hover:ring-accent/50"
                                onClick={(e) => { e.stopPropagation(); setPreviewImage(group.partner_avatar); }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="shrink-0 h-9 w-9 rounded-full bg-accent/10 text-accent flex items-center justify-center text-sm font-semibold">
                                {(group.partner_name || '?').charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <CardTitle className="text-base sm:text-lg truncate">{group.partner_name}</CardTitle>
                              <CardDescription className="truncate">{group.partner_email}</CardDescription>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap ml-8 sm:ml-0">
                            <Badge variant="secondary" className="text-sm sm:text-lg px-2 sm:px-4 py-1 sm:py-2">
                              {visiblePkgs.length}{query ? `/${group.package_count}` : ''} {language === 'en' ? 'packages' : 'colis'}
                            </Badge>
                            <Badge variant="outline" className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5 font-semibold">
                              {visiblePkgs.reduce((sum, p) => sum + (p.weight || 0), 0).toFixed(1)} kg
                            </Badge>
                            <Badge variant="outline" className="text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5 font-semibold text-green-700 border-green-300">
                              {visiblePkgs.reduce((sum, p) => sum + (p.total_price || p.price || 0), 0).toLocaleString('fr-FR')} €
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        {(() => {
                          const partnerId = group.partner_id;
                          const currentPartnerPage = packagePageByPartner[partnerId] || 1;
                          const currentPartnerItemsPerPage = packageItemsPerPartner[partnerId] || 10;
                          const totalPages = Math.ceil(visiblePkgs.length / currentPartnerItemsPerPage);
                          const paginatedPackages = visiblePkgs.slice(
                            (currentPartnerPage - 1) * currentPartnerItemsPerPage,
                            currentPartnerPage * currentPartnerItemsPerPage
                          );
                          
                          return (
                            <>
                              <div className="space-y-2 p-2 sm:p-6">
                                {paginatedPackages.map((pkg) => (
                                  <div
                                    key={pkg.id}
                                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2 sm:p-3 border rounded-lg ${
                                      selectedPackages.has(pkg.id) ? 'bg-blue-50 border-blue-300' : 'hover:bg-muted'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                                      {loadingMode === 'manual' && (
                                        <Checkbox
                                          checked={selectedPackages.has(pkg.id)}
                                          onCheckedChange={() => togglePackageSelection(pkg.id)}
                                          className="shrink-0"
                                        />
                                      )}
                                      <div className="min-w-0 flex-1">
                                        <p className="font-medium text-sm sm:text-base truncate">{pkg.oce_code}</p>
                                        <p className="text-xs sm:text-sm text-muted-foreground truncate">
                                          {pkg.sender_name} → {pkg.recipient_name}
                                        </p>
                                        <p className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                                          <CountryRoute origin={pkg.origin_country} destination={pkg.destination_country} size="sm" />
                                          <span>| {pkg.weight} kg</span>
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1 ml-7 sm:ml-0 shrink-0">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); setPhotosDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code }); }}
                                        title="Voir les photos"
                                      >
                                        <ImageIcon className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); printLabel(pkg); }}
                                        title="Imprimer étiquette"
                                      >
                                        <Printer className="h-4 w-4" />
                                      </Button>
                                      {canEditPackages && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="text-orange-600 hover:text-orange-700"
                                          onClick={(e) => { e.stopPropagation(); openPkgEditDialog(pkg); }}
                                          title="Modifier le colis"
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </Button>
                                      )}
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700"
                                        onClick={(e) => { e.stopPropagation(); setDeleteReason('duplicate'); setDeleteCustom(''); setDeleteDialog({ open: true, pkg }); }}
                                        title="Supprimer le colis"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                      <PackageStatusBadge status={pkg.status} size="sm" />
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {visiblePkgs.length > 10 && (
                                <Pagination
                                  currentPage={currentPartnerPage}
                                  totalPages={totalPages}
                                  itemsPerPage={currentPartnerItemsPerPage}
                                  totalItems={visiblePkgs.length}
                                  onPageChange={(page) => {
                                    setPackagePageByPartner(prev => ({ ...prev, [partnerId]: page }));
                                  }}
                                  onItemsPerPageChange={(value) => {
                                    setPackageItemsPerPartner(prev => ({ ...prev, [partnerId]: value }));
                                    setPackagePageByPartner(prev => ({ ...prev, [partnerId]: 1 }));
                                  }}
                                />
                              )}
                            </>
                          );
                        })()}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="shipments" className="space-y-4">
            {/* KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)', borderColor: '#cbd5e1' }}>
                <div className="analytics-kpi-dot" style={{ background: '#1a1a1a' }} />
                <span className="analytics-kpi-label">{language === 'en' ? 'Total Departures' : 'Total Départs'}</span>
                <span className="analytics-kpi-value">{shipmentKpis.total}</span>
              </div>
              <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #fff5f0 0%, #ffe8d6 100%)', borderColor: '#f0c8a8' }}>
                <div className="analytics-kpi-dot" style={{ background: '#e8590c' }} />
                <span className="analytics-kpi-label">{language === 'en' ? 'In Transit' : 'En Transit'}</span>
                <span className="analytics-kpi-value">{shipmentKpis.inTransit}</span>
              </div>
              <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #faf5ff 0%, #f0e4ff 100%)', borderColor: '#d4b8f0' }}>
                <div className="analytics-kpi-dot" style={{ background: '#b0b0b0' }} />
                <span className="analytics-kpi-label">{language === 'en' ? 'Arrived' : 'Arrivés'}</span>
                <span className="analytics-kpi-value">{shipmentKpis.arrived}</span>
              </div>
              <div className="analytics-kpi" style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', borderColor: '#a7d8b8' }}>
                <div className="analytics-kpi-dot" style={{ background: '#22c55e' }} />
                <span className="analytics-kpi-label">{language === 'en' ? 'Completed' : 'Terminés'}</span>
                <span className="analytics-kpi-value">{shipmentKpis.completed}</span>
              </div>
            </div>

            {/* Status Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {(language === 'en' ? [
                { key: 'all', label: 'All' },
                { key: 'scheduled', label: 'Scheduled' },
                { key: 'in_transit', label: 'In Transit' },
                { key: 'arrived', label: 'Arrived' },
                { key: 'awaiting_delivery', label: 'Awaiting Delivery' },
                { key: 'delivering', label: 'Delivering' },
                { key: 'completed', label: 'Completed' },
                { key: 'delayed', label: 'Delayed' },
                { key: 'cancelled', label: 'Cancelled' },
              ] : [
                { key: 'all', label: 'Tous' },
                { key: 'scheduled', label: 'Planifié' },
                { key: 'in_transit', label: 'En transit' },
                { key: 'arrived', label: 'Arrivé' },
                { key: 'awaiting_delivery', label: 'Attente livraison' },
                { key: 'delivering', label: 'Livraison en cours' },
                { key: 'completed', label: 'Terminé' },
                { key: 'delayed', label: 'En retard' },
                { key: 'cancelled', label: 'Annulé' },
              ])
                .filter(f => !HISTORY_STATUSES.includes(f.key))
                .filter(f => f.key === 'all' || (shipmentStatusCounts[f.key] || 0) > 0)
                .map(f => (
                  <Button
                    key={f.key}
                    variant={shipmentStatusFilter === f.key ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setShipmentStatusFilter(f.key); setCurrentPage(1); }}
                  >
                    {f.label} ({shipmentStatusCounts[f.key] || 0})
                  </Button>
                ))
              }
            </div>

            {/* Active package filter from dashboard */}
            {packageStatusFilter !== 'all' && (
              <div className="flex items-center gap-2 px-1">
                <Badge variant="outline" className="bg-accent/10 text-accent text-xs gap-1">
                  {language === 'en' ? 'Package filter' : 'Filtre colis'} : {(STATUS_LABELS[language] || STATUS_LABELS.fr)[packageStatusFilter] || packageStatusFilter}
                  <button
                    onClick={() => setPackageStatusFilter('all')}
                    className="ml-1 hover:text-foreground"
                  >
                    ✕
                  </button>
                </Badge>
              </div>
            )}

            <Card>
              <CardHeader>
                <CardTitle>{language === 'en' ? 'Departure List' : 'Liste des Départs'}</CardTitle>
                <CardDescription>{language === 'en' ? 'Sorted newest to oldest' : 'Triés du plus récent au plus ancien'}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {(() => {
                  const q = searchQuery.toLowerCase().trim();
                  let filteredShipments = q
                    ? activeShipments.filter(s =>
                        s.name?.toLowerCase().includes(q) ||
                        s.flight_name?.toLowerCase().includes(q) ||
                        s.origin_country?.toLowerCase().includes(q) ||
                        s.destination_country?.toLowerCase().includes(q) ||
                        s.notes?.toLowerCase().includes(q) ||
                        s.status?.toLowerCase().includes(q)
                      )
                    : activeShipments;

                  // Apply shipment status filter
                  if (shipmentStatusFilter !== 'all') {
                    filteredShipments = filteredShipments.filter(s => {
                      const normalized = s.status === 'planned' ? 'scheduled' : s.status;
                      return normalized === shipmentStatusFilter;
                    });
                  }

                  // Apply package status filter (from dashboard warnings)
                  if (packageStatusFilter !== 'all') {
                    filteredShipments = filteredShipments.filter(s => {
                      const sc = s.status_counts || {};
                      return (sc[packageStatusFilter] || 0) > 0;
                    });
                  }

                  return filteredShipments.length === 0 ? (
                  <EmptyState
                    icon={Ship}
                    title={q || shipmentStatusFilter !== 'all' ? t('noResults') : t('noShipmentsAvailable')}
                    description={q || shipmentStatusFilter !== 'all' ? (language === 'en' ? 'Try different search terms or filters' : 'Essayez avec d\'autres termes ou filtres') : (language === 'en' ? 'Create your first departure to group your packages' : 'Créez votre premier départ pour grouper vos colis')}
                    actionLabel={!q && shipmentStatusFilter === 'all' ? (language === 'en' ? 'Create a departure' : 'Créer un départ') : undefined}
                    onAction={!q && shipmentStatusFilter === 'all' ? () => setOpenCreateDialog(true) : undefined}
                  />
                ) : (
                  <>
                    <div className="space-y-3 p-2 sm:p-6">
                      {filteredShipments
                        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                        .map(shipment => renderShipmentCard(shipment))}
                    </div>
                    <Pagination
                      currentPage={currentPage}
                      totalPages={Math.ceil(filteredShipments.length / itemsPerPage)}
                      itemsPerPage={itemsPerPage}
                      totalItems={filteredShipments.length}
                      onPageChange={setCurrentPage}
                      onItemsPerPageChange={(value) => {
                        setItemsPerPage(value);
                        setCurrentPage(1);
                      }}
                    />
                  </>
                );
                })()}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{language === 'en' ? 'History' : 'Historique'}</CardTitle>
                <CardDescription>{language === 'en' ? 'Completed or cancelled departures' : 'Départs terminés ou annulés'}</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {(() => {
                  const q = searchQuery.toLowerCase().trim();
                  const filteredHistory = q
                    ? historyShipments.filter(s =>
                        s.name?.toLowerCase().includes(q) ||
                        s.flight_name?.toLowerCase().includes(q) ||
                        s.origin_country?.toLowerCase().includes(q) ||
                        s.destination_country?.toLowerCase().includes(q) ||
                        s.notes?.toLowerCase().includes(q) ||
                        s.status?.toLowerCase().includes(q)
                      )
                    : historyShipments;

                  return filteredHistory.length === 0 ? (
                    <EmptyState
                      icon={Ship}
                      title={q ? t('noResults') : (language === 'en' ? 'No archived departures' : 'Aucun départ archivé')}
                      description={q ? (language === 'en' ? 'Try different search terms' : 'Essayez avec d\'autres termes') : (language === 'en' ? 'Completed or cancelled departures will appear here' : 'Les départs terminés ou annulés apparaîtront ici')}
                    />
                  ) : (
                    <>
                      <div className="space-y-3 p-2 sm:p-6">
                        {filteredHistory
                          .slice((historyPage - 1) * itemsPerPage, historyPage * itemsPerPage)
                          .map(shipment => renderShipmentCard(shipment))}
                      </div>
                      <Pagination
                        currentPage={historyPage}
                        totalPages={Math.ceil(filteredHistory.length / itemsPerPage)}
                        itemsPerPage={itemsPerPage}
                        totalItems={filteredHistory.length}
                        onPageChange={setHistoryPage}
                        onItemsPerPageChange={(value) => {
                          setItemsPerPage(value);
                          setHistoryPage(1);
                        }}
                      />
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Shipment Dialog */}
        <Dialog open={openEditDialog} onOpenChange={setOpenEditDialog}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleUpdateShipment}>
              <DialogHeader>
                <DialogTitle>{t('editShipment')}</DialogTitle>
                <DialogDescription>
                  {language === 'en' ? 'Edit departure information before transit' : 'Modifiez les informations du départ avant sa mise en transit'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>{language === 'en' ? 'Departure name *' : 'Nom du départ *'}</Label>
                  <Input
                    value={editShipment.name}
                    onChange={(e) => setEditShipment({ ...editShipment, name: e.target.value })}
                    placeholder={language === 'en' ? 'Departure Paris-New York' : 'Départ Paris-New York'}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>{selectedShipment?.transport_mode === 'maritime' ? (language === 'en' ? 'Ship / Reference' : 'Navire / Référence') : (language === 'en' ? 'Flight / Tracking number' : 'Vol / Numéro de tracking')}</Label>
                  <div className="flex gap-2">
                    <Input
                      value={editShipment.flight_name}
                      onChange={(e) => setEditShipment({ ...editShipment, flight_name: e.target.value })}
                      placeholder={selectedShipment?.transport_mode === 'maritime' ? 'MSC OSCAR' : 'AF001'}
                      className="flex-1"
                    />
                    {selectedShipment && editShipment.flight_name && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={verifyingFlight}
                        onClick={() => handleVerifyFlight(selectedShipment.id, selectedShipment.name)}
                        className="whitespace-nowrap"
                      >
                        {verifyingFlight ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                        {language === 'en' ? 'Verify' : 'Vérifier'}
                      </Button>
                    )}
                  </div>
                  {editShipment.flight_name && selectedShipment?.transport_mode !== 'maritime' && (
                    <a
                      href={getFlightradarUrl(editShipment.flight_name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                    >
                      <ExternalLink className="h-3 w-3" />
                      {language === 'en' ? 'Track on Flightradar24' : 'Suivre sur Flightradar24'}
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('originCountry')}</Label>
                    <SearchableSelect
                      value={editShipment.origin_country}
                      onChange={(val) => setEditShipment({ ...editShipment, origin_country: val })}
                      placeholder={t('selectACountry') || 'Sélectionner un pays'}
                      searchPlaceholder={t('search') || 'Rechercher…'}
                      options={DEFAULT_COUNTRIES.map((c) => ({ value: c.name, label: `${getFlagEmoji(c.code)} ${c.name}` }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('destinationCountry')}</Label>
                    <SearchableSelect
                      value={editShipment.destination_country}
                      onChange={(val) => setEditShipment({ ...editShipment, destination_country: val })}
                      placeholder={t('selectACountry') || 'Sélectionner un pays'}
                      searchPlaceholder={t('search') || 'Rechercher…'}
                      options={DEFAULT_COUNTRIES.map((c) => ({ value: c.name, label: `${getFlagEmoji(c.code)} ${c.name}` }))}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('departureDate')}</Label>
                    <Input
                      type="datetime-local"
                      value={editShipment.departure_date}
                      onChange={(e) => setEditShipment((s) => ({
                        ...s,
                        departure_date: e.target.value,
                        deposit_deadline: s.deposit_deadline || depositDeadlineFromDeparture(e.target.value),
                      }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'en' ? 'Expected arrival' : 'Arrivée prévue'}</Label>
                    <Input
                      type="datetime-local"
                      value={editShipment.expected_arrival_date}
                      onChange={(e) => setEditShipment({ ...editShipment, expected_arrival_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{language === 'en' ? 'Package deposit deadline' : 'Date limite de dépôt des colis'}</Label>
                  <Input
                    type="datetime-local"
                    value={editShipment.deposit_deadline}
                    onChange={(e) => setEditShipment({ ...editShipment, deposit_deadline: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={editShipment.notes}
                    onChange={(e) => setEditShipment({ ...editShipment, notes: e.target.value })}
                    rows={3}
                    placeholder="Informations supplémentaires..."
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" type="button" onClick={() => setOpenEditDialog(false)}>{t('cancel')}</Button>
                <Button type="submit">{t('save')}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Shipment Details Dialog */}
        <Dialog open={openDetailsDialog} onOpenChange={setOpenDetailsDialog}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-2xl">
                <Ship className="h-6 w-6 text-blue-500" />
                {selectedShipment?.name}
              </DialogTitle>
              <DialogDescription>
                {language === 'en' ? 'Full departure details and package list' : 'Détails complets du départ et liste des colis'}
              </DialogDescription>
            </DialogHeader>

            {loadingDetails ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
              </div>
            ) : shipmentDetails ? (
              <div className="space-y-6 py-4">
                {/* Informations de la carréaison */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{language === 'en' ? 'General Information' : 'Informations Générales'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'en' ? 'Route' : 'Route'}</p>
                        <div className="font-medium"><CountryRoute origin={shipmentDetails.shipment.origin_country} destination={shipmentDetails.shipment.destination_country} /></div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'en' ? 'Status' : 'Statut'}</p>
                        <Badge className={
                          shipmentDetails.shipment.status === 'completed' ? 'bg-green-500' :
                          shipmentDetails.shipment.status === 'delivering' ? 'bg-blue-600' :
                          shipmentDetails.shipment.status === 'awaiting_delivery' ? 'bg-amber-500' :
                          shipmentDetails.shipment.status === 'in_progress' || shipmentDetails.shipment.status === 'in_transit' ? 'bg-accent' :
                          shipmentDetails.shipment.status === 'cancelled' ? 'bg-red-500' :
                          shipmentDetails.shipment.status === 'arrived' ? 'bg-green-600' :
                          shipmentDetails.shipment.status === 'delayed' ? 'bg-amber-500' : 'bg-muted-foreground'
                        }>
                          {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[shipmentDetails.shipment.status] || shipmentDetails.shipment.status}
                        </Badge>
                      </div>
                      {shipmentDetails.shipment.transport_mode && (
                        <div>
                          <p className="text-sm text-muted-foreground">Mode</p>
                          <Badge className={`${TRANSPORT_MODE_COLORS[shipmentDetails.shipment.transport_mode] || 'bg-blue-500 text-white'}`}>
                            {(TRANSPORT_MODE_LABELS[language] || TRANSPORT_MODE_LABELS.fr)[shipmentDetails.shipment.transport_mode] || shipmentDetails.shipment.transport_mode}
                          </Badge>
                        </div>
                      )}
                      {shipmentDetails.shipment.flight_name && (
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {shipmentDetails.shipment.transport_mode === 'maritime' ? (language === 'en' ? 'Ship' : 'Navire') : (language === 'en' ? 'Flight' : 'Vol')}
                          </p>
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">
                              {shipmentDetails.shipment.transport_mode === 'maritime' ? '🚢' : '✈️'} {shipmentDetails.shipment.flight_name}
                            </p>
                            <FlightVerificationBadge shipment={shipmentDetails.shipment} />
                            {shipmentDetails.shipment.transport_mode !== 'maritime' && (
                              <a
                                href={getFlightradarUrl(shipmentDetails.shipment.flight_name)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Flightradar24
                              </a>
                            )}
                            {!shipmentDetails.shipment.flight_verified && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 px-2 text-xs"
                                disabled={verifyingFlight}
                                onClick={() => handleVerifyFlight(shipmentDetails.shipment.id, shipmentDetails.shipment.name)}
                              >
                                {verifyingFlight ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                                {language === 'en' ? 'Verify' : 'Vérifier'}
                              </Button>
                            )}
                          </div>
                          {shipmentDetails.shipment.flight_data_snapshot?.airline && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {shipmentDetails.shipment.flight_data_snapshot.airline}
                              {shipmentDetails.shipment.flight_data_snapshot.departure_airport && ` — ${shipmentDetails.shipment.flight_data_snapshot.departure_airport}`}
                              {shipmentDetails.shipment.flight_data_snapshot.arrival_airport && ` → ${shipmentDetails.shipment.flight_data_snapshot.arrival_airport}`}
                            </p>
                          )}
                        </div>
                      )}
                      {!shipmentDetails.shipment.flight_name && (
                        <div>
                          <p className="text-sm text-muted-foreground">
                            {language === 'en' ? 'Flight' : 'Vol'}
                          </p>
                          <FlightVerificationBadge shipment={shipmentDetails.shipment} />
                        </div>
                      )}
                      {shipmentDetails.shipment.departure_date && (
                        <div>
                          <p className="text-sm text-muted-foreground">{t('departureDate')}</p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(shipmentDetails.shipment.departure_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                      )}
                      {shipmentDetails.shipment.expected_arrival_date && (
                        <div>
                          <p className="text-sm text-muted-foreground">{language === 'en' ? 'Expected arrival' : 'Arrivée prévue'}</p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(shipmentDetails.shipment.expected_arrival_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}
                          </p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'en' ? 'Creation date' : 'Date de création'}</p>
                        <p className="font-medium">{new Date(shipmentDetails.shipment.created_at).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'en' ? 'Package count' : 'Nombre de colis'}</p>
                        <p className="font-medium flex items-center gap-1">
                          <Package className="h-4 w-4" />
                          {shipmentDetails.total_packages}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{language === 'en' ? 'Total weight' : 'Poids total'}</p>
                        <p className="font-medium flex items-center gap-1">
                          <Weight className="h-4 w-4" />
                          {shipmentDetails.total_weight.toFixed(2)} kg
                        </p>
                      </div>
                    </div>
                    {shipmentDetails.shipment.notes && (
                      <div className="mt-4">
                        <p className="text-sm text-muted-foreground">Notes</p>
                        <p className="text-sm italic bg-muted p-2 rounded">{shipmentDetails.shipment.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Statistiques par statut */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{language === 'en' ? 'Status Distribution' : 'Répartition par Statut'}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Vue logistique : livrés / en livraison / reçus / non trouvés / non chargés */}
                    {(() => {
                      const pkgs = shipmentDetails.packages || [];
                      const delivered = pkgs.filter(p => p.status === 'delivered').length;
                      const outForDelivery = pkgs.filter(p => p.status === 'out_for_delivery').length;
                      const received = pkgs.filter(p => p.is_received && !['delivered', 'out_for_delivery'].includes(p.status)).length;
                      const notReceived = pkgs.filter(p => p.is_loaded && !p.is_received).length;
                      const notLoaded = pkgs.filter(p => !p.is_loaded).length;
                      const tiles = [
                        { label: language === 'en' ? 'Delivered' : 'Livrés', value: delivered, cls: 'bg-green-50 text-green-700 border-green-200' },
                        { label: language === 'en' ? 'Out for delivery' : 'En livraison', value: outForDelivery, cls: 'bg-amber-50 text-amber-700 border-amber-200' },
                        { label: language === 'en' ? 'Received (unloaded)' : 'Reçus (déchargés)', value: received, cls: 'bg-cyan-50 text-cyan-700 border-cyan-200' },
                        { label: language === 'en' ? 'Not found' : 'Non trouvés', value: notReceived, cls: 'bg-red-50 text-red-700 border-red-200' },
                        { label: language === 'en' ? 'Not loaded' : 'Non chargés', value: notLoaded, cls: 'bg-slate-50 text-slate-700 border-slate-200' },
                      ];
                      return (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                          {tiles.map(tile => (
                            <div key={tile.label} className={`rounded-lg border p-2.5 text-center ${tile.cls}`}>
                              <div className="text-xl font-bold">{tile.value}</div>
                              <div className="text-[11px] leading-tight mt-0.5">{tile.label}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    <div className="flex flex-wrap gap-3 pt-1 border-t border-border">
                      {Object.entries(shipmentDetails.status_counts).map(([status, count]) => (
                        <span key={status} className="inline-flex items-center gap-2">
                          <PackageStatusBadge status={status} size="sm" />
                          <span className="text-sm font-bold">{count}</span>
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Liste des colis */}
                <Card>
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <CardTitle className="text-lg">{language === 'en' ? `Package List (${shipmentDetails.packages.length})` : `Liste des Colis (${shipmentDetails.packages.length})`}</CardTitle>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportShipmentPackagesToCSV(shipmentDetails.packages, shipmentDetails.shipment.name)}
                        >
                          <Download className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">{language === 'en' ? 'Export CSV' : 'Exporter CSV'}</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const dep = shipmentDetails.shipment.departure_date;
                              const start = dep ? dep.slice(0, 10) : '2020-01-01';
                              const end = new Date().toISOString().slice(0, 10);
                              await downloadFile(
                                '/api/reports/shipments/excel',
                                `departs_${start}_${end}.xlsx`,
                                { start_date: start, end_date: end }
                              );
                            } catch {
                              // handled by api interceptor
                            }
                          }}
                        >
                          <FileSpreadsheet className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">{language === 'en' ? 'Export Excel' : 'Exporter Excel'}</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-blue-700 border-blue-300 hover:bg-blue-50"
                          onClick={() => setScanDialog({ open: true, checkpoint: 'loading', autoAssign: false })}
                        >
                          <PackageCheck className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">{language === 'en' ? 'Verify loading' : 'Vérifier le chargement'}</span>
                        </Button>
                        {(() => {
                          const awaitingPkgs = shipmentDetails.packages.filter(p => p.status === 'awaiting_customs');
                          if (awaitingPkgs.length === 0) return null;
                          return (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-accent border-accent/30 hover:bg-accent/10"
                              onClick={() => setCustomsBulkDialog({
                                open: true,
                                shipmentId: shipmentDetails.shipment.id,
                                shipmentName: shipmentDetails.shipment.name,
                                packageIds: awaitingPkgs.map(p => p.id),
                                note: ''
                              })}
                            >
                              <ShieldCheck className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">{language === 'en' ? `Clear all (${awaitingPkgs.length})` : `Dédouaner tout (${awaitingPkgs.length})`}</span>
                              <span className="sm:hidden">({awaitingPkgs.length})</span>
                            </Button>
                          );
                        })()}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {shipmentDetails.packages.map((pkg) => (
                        <div key={pkg.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border rounded-lg hover:bg-muted transition-all gap-3">
                          {/* Photo thumbnail */}
                          <div className="shrink-0 self-start">
                            {pkg.photo_url ? (
                              <div
                                className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden bg-muted cursor-pointer"
                                onClick={() => setPhotosDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code })}
                              >
                                <img src={pkg.photo_url} alt={pkg.oce_code} className="w-full h-full object-cover" />
                              </div>
                            ) : pkg.photo_count > 0 ? (
                              <div
                                className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-muted flex items-center justify-center cursor-pointer"
                                onClick={() => setPhotosDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code })}
                              >
                                <Camera className="h-5 w-5 text-muted-foreground" />
                              </div>
                            ) : (
                              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-muted/50 flex items-center justify-center">
                                <Package className="h-5 w-5 text-muted-foreground/50" />
                              </div>
                            )}
                          </div>

                          {/* Package info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-base truncate">{pkg.oce_code}</p>
                              {pkg.is_loaded && (
                                <Badge className="bg-blue-100 text-blue-700 text-[10px]">{language === 'en' ? 'Loaded' : 'Chargé'}</Badge>
                              )}
                              {pkg.is_received && (
                                <Badge className="bg-green-100 text-green-700 text-[10px]">{language === 'en' ? 'Received' : 'Reçu'}</Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground mt-1 truncate">
                              {pkg.sender_name} → {pkg.recipient_name}
                            </p>
                            <p className="text-sm text-muted-foreground flex items-center gap-1 flex-wrap">
                              <CountryRoute origin={pkg.origin_country} destination={pkg.destination_country} size="sm" />
                              <span>| {pkg.weight} kg{pkg.price != null ? ` | ${pkg.price.toFixed(2)} €` : ''}</span>
                            </p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              <Badge
                                className={
                                  pkg.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                                  pkg.payment_status === 'advance' ? 'bg-orange-100 text-orange-800' :
                                  pkg.is_paid ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }
                              >
                                {pkg.payment_status === 'paid' ? (language === 'en' ? 'Paid' : 'Payé') :
                                 pkg.payment_status === 'advance' ? `${language === 'en' ? 'Advance' : 'Avancé'}${pkg.advance_amount ? ` (${pkg.advance_amount}€)` : ''}` :
                                 pkg.is_paid ? (language === 'en' ? 'Paid' : 'Payé') : (language === 'en' ? 'Unpaid' : 'Non payé')}
                              </Badge>
                              {pkg.transport_mode && (
                                <Badge className={`${TRANSPORT_MODE_COLORS[pkg.transport_mode] || 'bg-blue-500 text-white'} text-xs`}>
                                  {pkg.transport_mode === 'aerien' ? <Plane className="h-3 w-3 mr-1" /> : <Ship className="h-3 w-3 mr-1" />}
                                  {(TRANSPORT_MODE_LABELS[language] || TRANSPORT_MODE_LABELS.fr)[pkg.transport_mode] || pkg.transport_mode}
                                </Badge>
                              )}
                              {pkg.partner_name && (
                                <Badge variant="outline" className="text-xs">
                                  {pkg.partner_name}
                                </Badge>
                              )}
                              {(() => {
                                const age = daysSinceUpdate(pkg.updated_at || pkg.created_at);
                                if (age.isStale) return <Badge className="bg-red-100 text-red-700 text-xs">{age.text}</Badge>;
                                return null;
                              })()}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-1 self-end sm:self-center shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPhotosDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code })}
                              title="Voir les photos"
                            >
                              <ImageIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => printLabel(pkg)}
                              title={language === 'en' ? 'Print label' : 'Imprimer l\'étiquette'}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            {canEditPackages && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-orange-600 hover:text-orange-700"
                                onClick={() => openPkgEditDialog(pkg)}
                                title={language === 'en' ? 'Edit package' : 'Modifier le colis'}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {pkg.status === 'awaiting_customs' && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-accent border-accent/30 hover:bg-accent/10"
                                onClick={() => setCustomsDialog({ open: true, packageId: pkg.id, oceCode: pkg.oce_code, note: '' })}
                                title="Dédouaner ce colis"
                              >
                                <ShieldCheck className="h-3.5 w-3.5 sm:mr-1" />
                                <span className="hidden sm:inline">{language === 'en' ? 'Clear customs' : 'Dédouaner'}</span>
                              </Button>
                            )}
                            {!['delivered', 'in_transit', 'arrived', 'customs_cleared'].includes(pkg.status) && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleRemoveFromShipment(pkg)}
                                title={language === 'en' ? 'Remove from departure (back to My packages)' : 'Retirer du départ (retour Mes colis)'}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {pkg.status !== 'delivered' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setPackageStatusDialog({
                                  open: true,
                                  packageId: pkg.id,
                                  oceCode: pkg.oce_code,
                                  currentStatus: pkg.status,
                                  newStatus: '',
                                  note: ''
                                })}
                                title="Changer le statut"
                              >
                                <RefreshCw className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <PackageStatusBadge status={pkg.status} size="sm" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : null}

            <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
              <div className="flex gap-2 flex-wrap">
                {selectedShipment && ['planned', 'scheduled'].includes(selectedShipment.status) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOpenDetailsDialog(false);
                      openEditShipment(selectedShipment);
                    }}
                  >
                    <Pencil className="h-4 w-4 sm:mr-2" />
                    <span className="hidden sm:inline">{t('edit')}</span>
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setOpenDetailsDialog(false);
                    setBroadcastDialog({ open: true, shipmentId: selectedShipment?.id, shipmentName: selectedShipment?.name });
                  }}
                >
                  <Megaphone className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{language === 'en' ? 'Broadcast' : 'Diffuser'}</span>
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={() => setOpenDetailsDialog(false)}>
                {t('close')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Package Photos Dialog */}
        <PackagePhotosDialog
          open={photosDialog.open}
          onOpenChange={(open) => setPhotosDialog(prev => ({ ...prev, open }))}
          packageId={photosDialog.packageId}
          oceCode={photosDialog.oceCode}
        />

        {/* Delete package dialog (admins) */}
        <Dialog open={deleteDialog.open} onOpenChange={(open) => { if (!open) setDeleteDialog({ open: false, pkg: null }); }}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Supprimer le colis {deleteDialog.pkg?.oce_code}</DialogTitle>
              <DialogDescription>
                Action réservée aux administrateurs. Le colis sera retiré (suppression logique) avec un motif tracé.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium">Motif</label>
                <select
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background"
                >
                  {Object.entries(DELETE_REASONS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </div>
              {deleteReason === 'other' && (
                <textarea
                  value={deleteCustom}
                  onChange={(e) => setDeleteCustom(e.target.value)}
                  placeholder="Précisez le motif…"
                  className="w-full border border-input rounded-md px-3 py-2 text-sm resize-none h-20"
                  maxLength={300}
                />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog({ open: false, pkg: null })}>Annuler</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={deleting || (deleteReason === 'other' && !deleteCustom.trim())}
                onClick={handleDeletePackage}
              >
                {deleting ? 'Suppression…' : 'Supprimer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Package Dialog */}
        <Dialog open={pkgEditDialog.open} onOpenChange={(open) => { if (!open) { setPkgEditDialog({ open: false, pkg: null }); setPkgEditForm({}); } }}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier le colis {pkgEditDialog.pkg?.oce_code}</DialogTitle>
              <DialogDescription>
                Corrigez les informations du colis. Le client sera informé de la mise à jour et la facture liée sera recalculée.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Nom expéditeur</label>
                <input value={pkgEditForm.sender_name || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, sender_name: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div>
                <label className="text-sm font-medium">Téléphone expéditeur</label>
                <input value={pkgEditForm.sender_phone || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, sender_phone: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium">Email expéditeur</label>
                <input type="email" value={pkgEditForm.sender_email || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, sender_email: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div>
                <label className="text-sm font-medium">Nom destinataire</label>
                <input value={pkgEditForm.recipient_name || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, recipient_name: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div>
                <label className="text-sm font-medium">Téléphone destinataire</label>
                <input value={pkgEditForm.recipient_phone || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, recipient_phone: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium">Email destinataire</label>
                <input type="email" value={pkgEditForm.recipient_email || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, recipient_email: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div>
                <label className="text-sm font-medium">Poids (kg)</label>
                <input type="number" min="0" step="0.01" value={pkgEditForm.weight ?? ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, weight: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div>
                <label className="text-sm font-medium">Pays de destination</label>
                <input value={pkgEditForm.destination_country || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, destination_country: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div>
                <label className="text-sm font-medium">Ville de destination</label>
                <input value={pkgEditForm.destination_city || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, destination_city: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm bg-background" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium">Description</label>
                <textarea value={pkgEditForm.description || ''} onChange={(e) => setPkgEditForm((f) => ({ ...f, description: e.target.value }))} className="w-full mt-1 border border-input rounded-md px-3 py-2 text-sm resize-none h-20 bg-background" maxLength={500} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setPkgEditDialog({ open: false, pkg: null }); setPkgEditForm({}); }}>Annuler</Button>
              <Button disabled={pkgEditSaving} onClick={handleEditPackage}>
                {pkgEditSaving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Shipment Broadcast Dialog */}
        <ShipmentBroadcastDialog
          open={broadcastDialog.open}
          onOpenChange={(open) => setBroadcastDialog(prev => ({ ...prev, open }))}
          shipmentId={broadcastDialog.shipmentId}
          shipmentName={broadcastDialog.shipmentName}
        />

        {selectedShipment && (
          <ShipmentScanDialog
            open={scanDialog.open}
            onOpenChange={(open) => {
              setScanDialog(prev => ({ ...prev, open }));
              if (!open) {
                // Rafraîchir les listes (colis par partenaire + départs).
                fetchData();
                // Ne ré-ouvrir le détail QUE s'il était déjà ouvert (évite la boucle
                // de chargement quand on scanne via « Démarrer un chargement »).
                if (openDetailsDialog && selectedShipment) fetchShipmentDetails(selectedShipment);
              }
            }}
            shipmentId={selectedShipment.id}
            checkpoint={scanDialog.checkpoint}
            autoAssign={scanDialog.autoAssign}
            // Rafraîchit les listes (colis par partenaire + départs) à chaque scan.
            // fetchData est silencieux (ne bascule pas le spinner plein écran) : pas de
            // boucle de chargement pendant le scan. Le détail du départ est rafraîchi à
            // la fermeture (fetchShipmentDetails a son propre spinner, à éviter par scan).
            onScanned={fetchData}
          />
        )}

        {/* Démarrer un chargement (mode auto) : choisir un départ puis scanner */}
        <Dialog open={startLoadingOpen} onOpenChange={setStartLoadingOpen}>
          <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{language === 'en' ? 'Start loading' : 'Démarrer un chargement'}</DialogTitle>
              <DialogDescription>{language === 'en' ? 'Choose the departure to load, then scan the packages.' : 'Choisissez le départ à charger, puis scannez les colis.'}</DialogDescription>
            </DialogHeader>
            {(() => {
              const list = shipments
                .filter(s => ['planned', 'scheduled'].includes(s.status))
                .sort((a, b) => {
                  const da = a.departure_date ? new Date(a.departure_date).getTime() : Infinity;
                  const db2 = b.departure_date ? new Date(b.departure_date).getTime() : Infinity;
                  return da - db2;
                });
              if (list.length === 0) {
                return <p className="text-sm text-muted-foreground py-4 text-center">{language === 'en' ? 'No departure available. Create one first.' : "Aucun départ disponible. Créez-en un d'abord."}</p>;
              }
              return (
                <div className="space-y-2 max-h-80 overflow-y-auto">
                  {list.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        setSelectedShipment(s);
                        setStartLoadingOpen(false);
                        setScanDialog({ open: true, checkpoint: 'loading', autoAssign: true });
                      }}
                      className="w-full text-left rounded-lg border border-border p-3 hover:border-accent hover:bg-accent/5 transition-all"
                    >
                      <div className="font-medium text-sm">{s.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {[s.origin_country, s.destination_country].filter(Boolean).join(' → ')}
                        {s.departure_date ? ` · ${new Date(s.departure_date).toLocaleDateString('fr-FR')}` : ''}
                      </div>
                    </button>
                  ))}
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>

        {/* Dialog dédouanement individuel */}
        <Dialog open={customsDialog.open} onOpenChange={(open) => { if (!open) setCustomsDialog(prev => ({ ...prev, open: false })); }}>
          <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{language === 'en' ? 'Clear package customs' : 'Dédouaner le colis'}</DialogTitle>
              <DialogDescription>
                {language === 'en' ? 'Confirm customs clearance for package' : 'Confirmer le dédouanement du colis'} <strong>{customsDialog.oceCode}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Label className="mb-1.5 block">{language === 'en' ? 'Note (optional)' : 'Note (optionnelle)'}</Label>
              <Textarea
                placeholder={language === 'en' ? 'Customs clearance details...' : 'Détails du dédouanement...'}
                value={customsDialog.note}
                onChange={(e) => setCustomsDialog(prev => ({ ...prev, note: e.target.value }))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCustomsDialog(prev => ({ ...prev, open: false }))}>{t('cancel')}</Button>
              <Button onClick={handleCustomsClearSingle} disabled={statusChanging} className="bg-accent hover:bg-accent/90">
                <ShieldCheck className="h-4 w-4 mr-1" />
                {statusChanging ? (language === 'en' ? 'Processing...' : 'En cours...') : (language === 'en' ? 'Confirm clearance' : 'Confirmer le dédouanement')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog dédouanement groupé */}
        <Dialog open={customsBulkDialog.open} onOpenChange={(open) => { if (!open) setCustomsBulkDialog(prev => ({ ...prev, open: false })); }}>
          <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{language === 'en' ? 'Bulk customs clearance' : 'Dédouanement groupé'}</DialogTitle>
              <DialogDescription>
                {language === 'en' ? 'Clear customs for' : 'Dédouaner'} <strong>{customsBulkDialog.packageIds.length}</strong> {language === 'en' ? 'packages from departure' : 'colis de la carréaison'} <strong>{customsBulkDialog.shipmentName}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="py-2">
              <Label className="mb-1.5 block">{language === 'en' ? 'Note (optional)' : 'Note (optionnelle)'}</Label>
              <Textarea
                placeholder={language === 'en' ? 'Bulk customs clearance details...' : 'Détails du dédouanement groupé...'}
                value={customsBulkDialog.note}
                onChange={(e) => setCustomsBulkDialog(prev => ({ ...prev, note: e.target.value }))}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCustomsBulkDialog(prev => ({ ...prev, open: false }))}>{t('cancel')}</Button>
              <Button onClick={handleCustomsClearBulk} disabled={statusChanging} className="bg-accent hover:bg-accent/90">
                <ShieldCheck className="h-4 w-4 mr-1" />
                {statusChanging ? (language === 'en' ? 'Processing...' : 'En cours...') : (language === 'en' ? `Clear ${customsBulkDialog.packageIds.length} packages` : `Dédouaner ${customsBulkDialog.packageIds.length} colis`)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog changement statut colis */}
        <Dialog open={packageStatusDialog.open} onOpenChange={(open) => { if (!open) setPackageStatusDialog(prev => ({ ...prev, open: false })); }}>
          <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{language === 'en' ? 'Change package status' : 'Changer le statut du colis'}</DialogTitle>
              <DialogDescription>
                {language === 'en' ? 'Package' : 'Colis'} <strong>{packageStatusDialog.oceCode}</strong> — {language === 'en' ? 'Current status' : 'Statut actuel'} : {(STATUS_LABELS[language] || STATUS_LABELS.fr)[packageStatusDialog.currentStatus] || packageStatusDialog.currentStatus}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="mb-1.5 block">{language === 'en' ? 'New status' : 'Nouveau statut'}</Label>
                <Select value={packageStatusDialog.newStatus || undefined} onValueChange={(val) => setPackageStatusDialog(prev => ({ ...prev, newStatus: val }))}>
                  <SelectTrigger><SelectValue placeholder={language === 'en' ? 'Select a status' : 'Sélectionner un statut'} /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(STATUS_LABELS.fr)
                      .filter(s => s !== packageStatusDialog.currentStatus)
                      .map(s => (
                        <SelectItem key={s} value={s}>
                          {(STATUS_LABELS[language] || STATUS_LABELS.fr)[s]}
                          {!(PACKAGE_TRANSITIONS[packageStatusDialog.currentStatus] || []).includes(s) && ' (admin)'}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">{language === 'en' ? 'Reason for change' : 'Raison du changement'} <span className="text-red-500">*</span></Label>
                <Textarea
                  placeholder={language === 'en' ? 'Explain the reason for this status change...' : 'Expliquez la raison de ce changement de statut...'}
                  value={packageStatusDialog.note}
                  onChange={(e) => setPackageStatusDialog(prev => ({ ...prev, note: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPackageStatusDialog(prev => ({ ...prev, open: false }))}>{t('cancel')}</Button>
              <Button onClick={handlePackageStatusChange} disabled={!packageStatusDialog.newStatus || !packageStatusDialog.note.trim() || statusChanging}>
                {statusChanging ? (language === 'en' ? 'Changing...' : 'Modification...') : t('confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog changement statut départ */}
        <Dialog open={shipmentStatusDialog.open} onOpenChange={(open) => { if (!open) setShipmentStatusDialog(prev => ({ ...prev, open: false })); }}>
          <DialogContent className="max-w-[calc(100vw-1rem)] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{language === 'en' ? 'Change departure status' : 'Changer le statut du départ'}</DialogTitle>
              <DialogDescription>
                {language === 'en' ? 'Departure' : 'Départ'} <strong>{shipmentStatusDialog.shipmentName}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="mb-1.5 block">{language === 'en' ? 'New status' : 'Nouveau statut'}</Label>
                <Select value={shipmentStatusDialog.newStatus || undefined} onValueChange={(val) => setShipmentStatusDialog(prev => ({ ...prev, newStatus: val }))}>
                  <SelectTrigger><SelectValue placeholder={language === 'en' ? 'Select a status' : 'Sélectionner un statut'} /></SelectTrigger>
                  <SelectContent>
                    {(SHIPMENT_TRANSITIONS[shipmentStatusDialog.currentStatus] || []).map(s => (
                      <SelectItem key={s} value={s}>
                        {(SHIPMENT_STATUS_LABELS[language] || SHIPMENT_STATUS_LABELS.fr)[s] || s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">{language === 'en' ? 'Note (required)' : 'Note (obligatoire)'}</Label>
                <Textarea
                  placeholder={language === 'en' ? 'Reason for change...' : 'Raison du changement...'}
                  value={shipmentStatusDialog.note}
                  onChange={(e) => setShipmentStatusDialog(prev => ({ ...prev, note: e.target.value }))}
                  rows={2}
                />
              </div>
              {/* Cascade warning */}
              {shipmentStatusDialog.newStatus && (
                <Alert className="border-amber-300 bg-amber-500/10">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-sm text-amber-800">
                    {shipmentStatusDialog.newStatus === 'cancelled'
                      ? (language === 'en' ? 'All packages will be detached and returned to their collector (in "My packages").' : 'Tous les colis seront détachés et reviendront chez leur collecteur (dans « Mes colis »).')
                      : shipmentStatusDialog.newStatus === 'in_transit'
                      ? (language === 'en' ? 'All packages in this departure will be set to "In Transit" status.' : 'Tous les colis de ce départ passeront au statut "En transit".')
                      : shipmentStatusDialog.newStatus === 'arrived'
                      ? (language === 'en' ? 'All packages will be set to "Arrived" then "Awaiting Customs Clearance" status.' : 'Tous les colis passeront au statut "Arrivé" puis "En attente de dédouanement".')
                      : shipmentStatusDialog.newStatus === 'awaiting_delivery'
                      ? (language === 'en' ? 'All packages will be set to "Awaiting Delivery" status.' : 'Tous les colis passeront au statut "En attente de livraison".')
                      : shipmentStatusDialog.newStatus === 'delivering'
                      ? (language === 'en' ? 'The departure will be marked as "Delivering".' : 'Le départ sera marqué "Livraison en cours".')
                      : shipmentStatusDialog.newStatus === 'completed'
                      ? (language === 'en' ? 'The departure will be marked as "Completed".' : 'Le départ sera marqué "Terminé".')
                      : (language === 'en' ? 'This status change will be applied to the departure.' : 'Ce changement de statut sera appliqué au départ.')}
                  </AlertDescription>
                </Alert>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShipmentStatusDialog(prev => ({ ...prev, open: false }))}>{t('cancel')}</Button>
              <Button
                onClick={handleShipmentStatusChange}
                disabled={!shipmentStatusDialog.newStatus || !shipmentStatusDialog.note.trim() || statusChanging}
                variant={shipmentStatusDialog.newStatus === 'cancelled' ? 'destructive' : 'default'}
              >
                {statusChanging ? (language === 'en' ? 'Changing...' : 'Modification...') : (language === 'en' ? 'Confirm change' : 'Confirmer le changement')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Aperçu plein écran d'une photo de profil */}
        {previewImage && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4 cursor-zoom-out"
            onClick={() => setPreviewImage(null)}
          >
            <img
              src={previewImage}
              alt=""
              className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 h-10 w-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Fermer"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>
        )}
      </div>
    </ProfessionalLayout>
  );
};

export default ShipmentManagement;
